import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const INTAKE_URL = import.meta.env.VITE_INTAKE_URL

const TIPO_CODIGO = 'raiox_donodependencia'

const diag = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  db: { schema: 'diagnostico' },
  auth: { persistSession: false },
})

/**
 * Carrega a config do diagnostico adaptativo: o tipo (com config), as
 * perguntas de triagem (eh_filtro) e os blocos de aprofundamento agrupados
 * por gargalo (condicao_exibicao.gargalo).
 */
export async function loadConfig() {
  const { data: tipo, error: eTipo } = await diag
    .from('diagnostico_tipos')
    .select('id, codigo, nome, config')
    .eq('codigo', TIPO_CODIGO)
    .eq('ativo', true)
    .single()
  if (eTipo) throw eTipo

  const { data: dims, error: eDim } = await diag
    .from('diagnostico_dimensoes')
    .select('id, ordem')
    .eq('tipo_id', tipo.id)
    .order('ordem')
  if (eDim) throw eDim

  const { data: perguntas, error: ePerg } = await diag
    .from('diagnostico_perguntas')
    .select('id, codigo, texto, eh_filtro, condicao_exibicao, ordem')
    .in(
      'dimensao_id',
      dims.map((d) => d.id),
    )
    .eq('ativo', true)
    .order('ordem')
  if (ePerg) throw ePerg

  const { data: opcoes, error: eOpc } = await diag
    .from('diagnostico_opcoes')
    .select('id, codigo, texto, pontos, metadata, pergunta_id, ordem')
    .in(
      'pergunta_id',
      perguntas.map((p) => p.id),
    )
    .order('ordem')
  if (eOpc) throw eOpc

  const opcByPerg = groupBy(opcoes, 'pergunta_id')
  const comOpcoes = (p) => ({
    codigo: p.codigo,
    texto: p.texto,
    gargalo: p.condicao_exibicao?.gargalo ?? null,
    opcoes: (opcByPerg[p.id] ?? []).map((o) => ({
      codigo: o.codigo,
      texto: o.texto,
      vota: o.metadata?.vota ?? null,
    })),
  })

  const triagem = perguntas.filter((p) => p.eh_filtro).map(comOpcoes)
  const blocos = {}
  for (const p of perguntas.filter((p) => !p.eh_filtro)) {
    const g = p.condicao_exibicao?.gargalo
    if (!g) continue
    if (!blocos[g]) blocos[g] = []
    blocos[g].push(comOpcoes(p))
  }

  return { tipo, config: tipo.config, triagem, blocos }
}

/**
 * Envia as respostas pra edge function. Ela classifica o gargalo, mede a
 * gravidade, gera o parecer e devolve o resultado pronto.
 */
export async function submitDiagnostico(payload) {
  const res = await fetch(INTAKE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(payload),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'Falha ao enviar o diagnostico. Tente de novo.')
  }
  return data.resultado
}

function groupBy(rows, key) {
  return rows.reduce((acc, row) => {
    const k = row[key]
    if (!acc[k]) acc[k] = []
    acc[k].push(row)
    return acc
  }, {})
}
